<template>
  <section class="request-payment-step">
    <a-alert v-if="paymentSystemsLoading" type="info" show-icon message="Loading payment systems" />
    <a-alert
      v-if="processingError"
      type="error"
      show-icon
      message="Payment processing could not continue"
      :description="processingError"
    />
    <a-alert v-if="stripeLoading" type="info" show-icon message="Checking your company Stripe account" />
    <a-alert
      v-else-if="stripeError"
      type="error"
      show-icon
      message="Stripe status could not be checked"
      description="Retry the check or choose Manual for this request."
    />
    <a-alert v-else-if="!stripeReady" type="warning" show-icon :message="stripeMessage.title" :description="stripeMessage.description" />

    <button
      type="button"
      class="payment-system-choice"
      :class="{ 'payment-system-choice--selected': paymentSystem === 'stripe' }"
      :disabled="!canSelectStripe || !isStripeAvailable"
      @click="selectPaymentSystem('stripe')"
    >
      <span>
        <strong class="stripe-wordmark">stripe</strong>
        <small>Request payment through Stripe.</small>
      </span>
      <a-tag :color="isStripeAvailable ? '#10b981' : '#f59e0b'">
        {{ isStripeAvailable ? 'Available' : 'Unavailable' }}
      </a-tag>
    </button>

    <div v-if="paymentSystem === 'stripe' && !isStripeAvailable" class="stripe-actions">
      <a-button type="primary" :loading="stripeSetupLoading" @click="openStripeSetup">
        {{ stripeAccountExists ? 'Continue Stripe setup' : 'Set up Stripe' }}
      </a-button>
      <a-button :loading="stripeLoading" @click="refreshStripeStatus">Refresh status</a-button>
    </div>

    <button
      type="button"
      class="payment-system-choice"
      :class="{ 'payment-system-choice--selected': paymentSystem === 'manual' }"
      :disabled="!canSelectPaymentSystem || !isManualAvailable"
      @click="selectPaymentSystem('manual')"
    >
      <span>
        <strong>Manual</strong>
        <small>Request payment outside Stripe.</small>
      </span>
      <a-tag :color="isManualAvailable ? '#10b981' : '#f59e0b'">
        {{ isManualAvailable ? 'Available' : 'Unavailable' }}
      </a-tag>
    </button>

    <div v-if="showManualMethod" class="manual-method">
      <label>Payment method</label>
      <a-select :value="manualMethod" @change="manualMethod = $event">
        <a-select-option v-for="method in availableManualMethods" :key="method" :value="method">
          {{ paymentMethodsMap[method].label }}
        </a-select-option>
      </a-select>
    </div>
  </section>
</template>

<script>
import { Alert, Button, Select, Tag } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import StripeNotificationMixin from '../../mixins/StripeNotificationMixin'
import { isStripeAccountReady } from '@/store/modules/stripe/getters'
import { isStripeV2PaymentSystem } from '@/store/modules/payment/constants'
import { paymentMethodsMap } from '@/const'

const { mapActions: paymentActions } = createNamespacedHelpers('payment')
const { mapActions: stripeActions } = createNamespacedHelpers('stripe')

export default {
  mixins: [StripeNotificationMixin],
  components: {
    'a-alert': Alert,
    'a-button': Button,
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-tag': Tag
  },
  props: {
    paymentId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      paymentSystems: [],
      paymentSystemsLoading: false,
      paymentSystem: 'stripe',
      manualMethod: 'bank_transfer',
      nextAction: '',
      processingError: '',
      isProcessing: false,
      stripeLoading: false,
      stripeSetupLoading: false,
      stripeError: '',
      stripeReady: false,
      stripeAccountExists: false,
      paymentMethodsMap
    }
  },
  computed: {
    currentProjectId() {
      return this.$store.state.applicationState.currentProject?.id
    },
    isStripeAvailable() {
      return this.paymentSystems.some(
        system => system.available && system.template == null && (system.name === 'Stripe V2' || isStripeV2PaymentSystem(system))
      )
    },
    isManualAvailable() {
      return this.paymentSystems.some(system => system.available && (system.template === 4 || system.name === 'Manual'))
    },
    selectedSystem() {
      return this.paymentSystems.find(system => {
        if (!system.available) return false
        if (this.paymentSystem === 'manual') return system.template === 4 || system.name === 'Manual'
        return system.template == null && (system.name === 'Stripe V2' || isStripeV2PaymentSystem(system))
      })
    },
    canSelectPaymentSystem() {
      return !this.paymentSystemsLoading && !this.isProcessing && this.nextAction === 'start_processing'
    },
    canSelectStripe() {
      return this.canSelectPaymentSystem && !this.stripeLoading
    },
    availableManualMethods() {
      return Object.keys(this.paymentMethodsMap)
    },
    showManualMethod() {
      return this.paymentSystem === 'manual' && ['start_processing', 'select_method'].includes(this.nextAction)
    },
    canProcess() {
      if (this.isProcessing || this.paymentSystemsLoading || !this.nextAction) return false
      if (this.nextAction === 'select_method') return this.availableManualMethods.includes(this.manualMethod)
      if (this.nextAction !== 'start_processing' || !this.selectedSystem) return false
      if (this.paymentSystem === 'manual') return this.availableManualMethods.includes(this.manualMethod)
      return this.stripeReady
    },
    stepState() {
      return { canContinue: this.canProcess, loading: this.isProcessing, label: 'Continue' }
    },
    stripeMessage() {
      if (!this.stripeAccountExists) {
        return {
          title: 'Set up Stripe for your company',
          description: 'Complete your company setup, then return to finish this request. Manual remains available.'
        }
      }
      return {
        title: 'Continue your Stripe setup',
        description: 'Complete the remaining Stripe steps, then refresh the status.'
      }
    }
  },
  watch: {
    stepState: {
      handler(state) {
        this.$emit('state-change', state)
      },
      immediate: true
    }
  },
  mounted() {
    this.loadPaymentSystems()
    this.refreshStripeStatus()
  },
  methods: {
    ...paymentActions(['getPaymentRequestSystems', 'getPaymentRequestAction', 'doPaymentRequestAction']),
    ...stripeActions(['getProjectAccountStatus', 'createProjectOnboardingLink']),
    async loadPaymentSystems() {
      this.paymentSystemsLoading = true
      this.processingError = ''
      try {
        const systems = await this.getPaymentRequestSystems({ payment_id: this.paymentId })
        this.paymentSystems = Array.isArray(systems) ? systems : []
        await this.loadNextAction()
      } catch (error) {
        this.processingError = typeof error === 'string' ? error : error?.message
      } finally {
        this.paymentSystemsLoading = false
      }
    },
    async selectPaymentSystem(system) {
      if (!this.canSelectPaymentSystem) return
      this.paymentSystem = system
      this.processingError = ''
    },
    async processPayment() {
      if (!this.canProcess) return
      if (this.nextAction === 'start_processing') {
        await this.startProcessing()
        if (this.nextAction === 'select_method') await this.selectManualMethod()
        return
      }
      await this.selectManualMethod()
    },
    async selectManualMethod() {
      if (this.nextAction !== 'select_method' || !this.availableManualMethods.includes(this.manualMethod)) return
      this.isProcessing = true
      this.processingError = ''
      try {
        await this.doPaymentRequestAction({
          payment_id: this.paymentId,
          action: 'select_method',
          payload: { method: this.manualMethod }
        })
        await this.loadNextAction()
      } catch (error) {
        this.processingError = typeof error === 'string' ? error : error?.message
      } finally {
        this.isProcessing = false
      }
    },
    async startProcessing() {
      if (!this.selectedSystem) return
      this.isProcessing = true
      this.processingError = ''
      try {
        await this.doPaymentRequestAction({
          payment_id: this.paymentId,
          action: 'start_processing',
          payload: { payment_system_id: this.selectedSystem.id }
        })
        await this.loadNextAction()
      } catch (error) {
        this.processingError = typeof error === 'string' ? error : error?.message
      } finally {
        this.isProcessing = false
      }
    },
    async loadNextAction() {
      const action = await this.getPaymentRequestAction({ payment_id: this.paymentId })
      this.nextAction = action || ''
      if (action === 'start_processing') return
      if (action === 'select_method') {
        this.paymentSystem = 'manual'
        return
      }
      if (action === 'confirm_transfer') {
        this.$emit('processed')
        return
      }
      throw new Error(`The payment request needs an unsupported next action: ${action || 'unknown'}.`)
    },
    async refreshStripeStatus() {
      this.stripeLoading = true
      this.stripeError = ''
      try {
        const result = await this.getProjectAccountStatus({ project_id: this.currentProjectId })
        this.stripeAccountExists = Boolean(result.available && result.account?.exists)
        this.stripeReady = Boolean(result.available && isStripeAccountReady(result.account))
      } catch (error) {
        this.stripeError = typeof error === 'string' ? error : error?.message
        this.stripeReady = false
      } finally {
        this.stripeLoading = false
      }
    },
    async openStripeSetup() {
      const externalWindow = window.open('', '_blank')
      if (!externalWindow) {
        this.showErrorNotification('Allow pop-ups for this site and try again.')
        return
      }
      externalWindow.opener = null
      this.stripeSetupLoading = true
      try {
        const result = await this.createProjectOnboardingLink({ project_id: this.currentProjectId })
        if (!result.available || !result.url) {
          externalWindow.close()
          this.showErrorNotification('Stripe setup is not available for this company.')
          return
        }
        externalWindow.location.replace(result.url)
      } catch (error) {
        externalWindow.close()
        this.showErrorNotification(error, 'Stripe setup could not be started. Try again.')
      } finally {
        this.stripeSetupLoading = false
      }
    }
  }
}
</script>

<style scoped>
.request-payment-step, .payment-system-choice > span, .manual-method { display: flex; flex-direction: column; }
.request-payment-step { gap: 16px; }
.payment-system-choice { width: 100%; min-height: 76px; padding: 16px; display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; border: 1px solid var(--border-color-base); border-radius: 8px; background: var(--bg-card); color: var(--text-color); text-align: left; cursor: pointer; }
.payment-system-choice--selected { border-color: var(--primary-color); box-shadow: 0 0 0 1px var(--primary-color); }
.payment-system-choice small { margin-top: 4px; color: var(--text-secondary); }
.payment-system-choice strong:not(.stripe-wordmark) { font-size: 16px; }
.stripe-wordmark { color: #635bff; font-size: 24px; letter-spacing: -1px; }
.stripe-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.manual-method { gap: 6px; }
.manual-method label { font-weight: 600; }
</style>
